/**
 * External analytics page: loads the facility's analytics URL from Firestore
 * and displays it in a full-height iframe (same flow as medonc-dashboard).
 */
import { useCallback, useEffect, useState } from "react";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../api/firebase/references";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { HealthCareFacilityModel } from "../../data-model/healthFacility";
import { LocalStorageKeys } from "../../enums/localStorageKeys";
import { getItemFromLocalStorage } from "../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";

/**
 * Loads the best facility document for embedded analytics: prefers the ID used
 * for external APIs, then falls back to the portal healthcare facility when the
 * primary doc has no `analytics_url` (often configured only on the external org).
 *
 * @param primaryFacilityId - First Firestore `healthcare_facility` document id to try
 * @param portalHealthcareFacilityId - Logged-in portal facility id from localStorage
 * @returns Facility payload to read `analytics_url` from, or undefined if none found
 */
async function resolveFacilityForAnalyticsUrl(
  primaryFacilityId: string,
  portalHealthcareFacilityId: string,
): Promise<HealthCareFacilityModel | undefined> {
  if (!primaryFacilityId) {
    return undefined;
  }
  const primary = await FirestoreService.getDocument<HealthCareFacilityModel>(
    FirestoreDocumentReference.healthFacility(primaryFacilityId),
  );
  if (primary?.analytics_url?.trim()) {
    return primary;
  }
  if (
    portalHealthcareFacilityId &&
    portalHealthcareFacilityId !== primaryFacilityId
  ) {
    return FirestoreService.getDocument<HealthCareFacilityModel>(
      FirestoreDocumentReference.healthFacility(portalHealthcareFacilityId),
    );
  }
  return primary;
}

/**
 * Fetches the logged-in facility document and renders its analytics iframe.
 *
 * @returns Analytics layout with loading, error, empty, and embedded dashboard states
 */
const AnalyticsNew = () => {
  const [healthFacilityDataState, setHealthFacilityDataState] = useState<{
    data?: HealthCareFacilityModel;
    loading: boolean;
    error: string | null;
  }>({
    data: undefined,
    loading: false,
    error: null,
  });

  const orgId = getOrgIdForFetchExternalWorklist();
  const healthcareFacilityId =
    getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID) ?? "";

  const fetchHealthFacilityData = useCallback(async () => {
    setHealthFacilityDataState({ data: undefined, loading: true, error: null });
    try {
      const healthFacilityData = await resolveFacilityForAnalyticsUrl(
        orgId,
        healthcareFacilityId,
      );
      setHealthFacilityDataState({
        data: healthFacilityData,
        loading: false,
        error: null,
      });
    } catch (e) {
      setHealthFacilityDataState({
        data: undefined,
        loading: false,
        error: e instanceof Error ? e.message : "Failed to load analytics.",
      });
    }
  }, [orgId, healthcareFacilityId]);

  useEffect(() => {
    void fetchHealthFacilityData();
  }, [fetchHealthFacilityData]);

  const analyticsUrl =
    healthFacilityDataState?.data?.analytics_url?.trim() ?? "";

  return (
    <div className="new-analytics-page--layout flex h-full min-h-0 w-full min-w-0 flex-1 flex-col rounded-lg bg-primaryGray-16 p-4">
      {healthFacilityDataState?.loading ? (
        <div className="flex h-full w-full flex-1 items-center justify-center">
          <LoaderMessage message="Loading analytics..." />
        </div>
      ) : healthFacilityDataState?.error ? (
        <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          <div className="max-w-md rounded border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-800">
              Could not load analytics
            </p>
            <p className="mt-2 text-sm text-red-700">
              {healthFacilityDataState.error}
            </p>
          </div>
        </div>
      ) : analyticsUrl ? (
        <iframe
          title="Analytics"
          className="min-h-0 w-full flex-1 border-0"
          src={analyticsUrl}
          allowFullScreen={true}
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-sm text-gray-600">
          <p>No analytics URL is configured for this facility.</p>
          <p className="max-w-md">
            Add <code className="rounded bg-gray-200 px-1">analytics_url</code>{" "}
            on the healthcare facility document in Firestore (portal or internal
            org, depending on your setup).
          </p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsNew;
